import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { SpaceEditMode } from '@gitbook/api-client';
import { createHTTPError } from '@gitbook/util-debug';

import { OrderIndexSchema } from './collections';
import { ContentSharingSchema, URLSchema } from './content';
import { Timestamp } from './db/firestore';
import { UserContributionsSchema } from './helpers';
import { IntegrationInstallationMapSchema } from './integrations/installations';

export const SPACE_TITLE_LIMIT = 50;

export const SPACE_DEFAULT_TITLE = 'Untitled';

/**
 * Git sync properties, declared here to prevent a dependency cycle.
 */
export const GitSyncProviderTypeGitHubSchema = z.literal(api.GitSyncProvider.Github);
export const GitSyncProviderTypeGitLabSchema = z.literal(api.GitSyncProvider.Gitlab);
export const GitSyncProviderTypeGitHubLegacySchema = z.literal(api.GitSyncProvider.GithubLegacy);
export const GitSyncProviderTypeSchema = z.union([
    GitSyncProviderTypeGitHubSchema,
    GitSyncProviderTypeGitLabSchema,
    GitSyncProviderTypeGitHubLegacySchema,
]);
export type GitSyncProviderType = z.infer<typeof GitSyncProviderTypeSchema>;

export const GitSyncOperationImportSchema = z.object({
    id: z.string(),
    direction: z.literal('import'),
    importCommit: z.string(),
});
export type GitSyncOperationImport = z.infer<typeof GitSyncOperationImportSchema>;

export const GitSyncOperationExportSchema = z.object({
    id: z.string(),
    direction: z.literal('export'),
    workingExportRef: z.string(),
    exportCommit: z.string().optional(),
});
export type GitSyncOperationExport = z.infer<typeof GitSyncOperationExportSchema>;

/**
 * State of a gitsync operation stored on the space object.
 */
export const GitSyncOperationStateSchema = z.enum(['running', 'failure', 'success']);
export type GitSyncOperationState = z.infer<typeof GitSyncOperationStateSchema>;

export const GitSyncOperationSchema = z
    .object({
        state: GitSyncOperationStateSchema,
        startedAt: z.instanceof(Timestamp).optional(),
        completedAt: z.instanceof(Timestamp).optional(),
        error: z.string().optional(),
    })
    .and(z.union([GitSyncOperationImportSchema, GitSyncOperationExportSchema]));
export type GitSyncOperation = z.infer<typeof GitSyncOperationSchema>;

/**
 * Reason when a space is blocked via the backoffice
 */
export const SpaceBlockBackOfficeReasonSchema = z.enum(['DMCA']);
export type SpaceBlockBackOfficeReason = z.infer<typeof SpaceBlockBackOfficeReasonSchema>;

// keyof protos.google.cloud.webrisk.v1.ThreatType
const URLThreatTypeSchema = z.enum([
    'THREAT_TYPE_UNSPECIFIED',
    'MALWARE',
    'SOCIAL_ENGINEERING',
    'UNWANTED_SOFTWARE',
]);

/**
 * Reason for a space to be blocked.
 */
export const SpaceBlockReasonSchema = z.union([
    URLThreatTypeSchema,
    SpaceBlockBackOfficeReasonSchema,
]);
export type SpaceBlockReason = z.infer<typeof SpaceBlockReasonSchema>;

const SpaceSearchKeySchema = z.union(
    [
        z.object({
            /**
             * Version of the search key, increased each time the visibility is changed
             */
            searchKeyVersion: z.number(),
            /**
             * Algolia search key
             */
            searchKey: z.string(),
        }),
        z.object({
            searchKey: z.undefined().optional(),
            searchKeyVersion: z.undefined().optional(),
        }),
    ],
    {
        error: (err) => {
            if (err.code === z.ZodIssueCode.invalid_union) {
                return 'searchKey and searchKeyVersion should be defined together';
            }

            return;
        },
    }
);

const SpaceWithGitSchema = z.union(
    [
        z.object({
            /**
             * How should this space be edited ?
             * Always set to locked when git is enabled.
             */
            editMode: z.literal(SpaceEditMode.Locked),
            /**
             * Git information, if GitSync is enabled for this space.
             */
            git: z.object({
                /**
                 * If this Space is linked to an installation, this field contains the provider
                 * of the installation.
                 */
                installationProvider: GitSyncProviderTypeSchema.optional(),
                /**
                 * Currently running operation in the Git sync.
                 */
                operation: GitSyncOperationSchema.optional(),
                /**
                 * The URL to the repository, used when rendering a public content
                 */
                url: z.string().optional(),
                /**
                 * Whether the git sync is enabled for this space via an integration
                 */
                integration: z.string().optional(),
                /**
                 *  When was the git info last updated for this space
                 */
                updatedAt: z.instanceof(Timestamp).optional(),
            }),
        }),
        z.object({
            /**
             * How should this space be edited ?
             */
            editMode: z.enum(SpaceEditMode),
            git: z.undefined().optional(),
        }),
    ],
    {
        error: (err) => {
            if (err.code === z.ZodIssueCode.invalid_union) {
                return `editMode must be "${SpaceEditMode.Locked}" when git is defined`;
            }

            return;
        },
    }
);

const SpaceWithVisitorAuthSchema = z.union([
    z.object({
        /**
         * @deprecated Should not be used anymore. Use `resolveSpacePublishedInfo` instead.
         */
        visitorAuth: z.object({
            backend: z.enum(['custom', 'integration']).default('custom'),
            integration: z.string().optional(),
        }),
    }),
    z.object({
        /**
         * @deprecated Should not be used anymore. Use `resolveSpacePublishedInfo` instead.
         */
        visitorAuth: z.undefined().optional(),
    }),
]);

const SpaceMergeRulesSchema = z.custom<api.MergeRulesSpaceConfiguration>();

export const SpaceSchema = ContentSharingSchema.extend({
    key: z.string(),
    title: z.string().max(SPACE_TITLE_LIMIT),
    /**
     * Index to order the space with others.
     */
    orderIndex: OrderIndexSchema,
    /**
     * Selected emoji for this space, if one has been selected.
     */
    emoji: z.string().optional(),
    /**
     * The url for a custom icon for this space, if one has been selected.
     */
    iconURL: URLSchema.optional(),
    /**
     * Language of the content in this space.
     * We use the prop name "contentLanguage" as "language" was used by the now deprecated property (before sites)
     */
    contentLanguage: z.enum(api.TranslationLanguage).optional(),
    /**
     * Contributions per users on this space.
     * @deprecated use Hive if you want to know space contributions.
     */
    edits: UserContributionsSchema,
    /**
     * Comments per users on this space.
     */
    comments: UserContributionsSchema,
    /**
     * Content displayed in the space.
     * @deprecated a space's current revision should come from Hive. See querySpace for an example of how to fetch this.
     */
    revision: z.string(),
    /**
     * Last revision indexed in Algolia
     */
    indexedRevision: z.string().optional(),
    /**
     * Last revision indexed in the vector search engine
     */
    aiIndexedRevision: z.string().optional(),
    /**
     * Last revision audited
     * @deprecated
     */
    aiAuditedRevision: z.string().optional(),
    /**
     * Last revision where content refs were indexed
     */
    contentRefIndexedRevision: z.string().optional(),
    /**
     * Timestamp of when the space was blocked.
     * Undefined if it's not blocked.
     * (Boolean for older records, still supported for backward compatibility.)
     */
    blocked: z.union([z.boolean(), z.instanceof(Timestamp)]).optional(),
    /**
     * Reasons for which a space is blocked.
     */
    blockedThreats: z.array(SpaceBlockReasonSchema).optional(),
    /**
     * Number of comments on this space (non resolved).
     */
    openComments: z.number().optional(),
    /**
     * Total number of change requests on this space.
     */
    changeRequests: z.number().optional(),
    /**
     * Number of change requests on this space (in open state).
     */
    openChangeRequests: z.number().optional(),
    /**
     * Number of change requests on this space (in draft state).
     */
    draftChangeRequests: z.number().optional(),
    /**
     * When defined, the space has been soft-deleted.
     * It'll be hard-deleted 7-days after this date.
     */
    deletedAt: z.instanceof(Timestamp).optional(),
    /**
     * When defined, indicates the user who soft-deleted the space.
     */
    deletedBy: z.string().optional(),
    /**
     * When this space was created
     */
    createdAt: z.instanceof(Timestamp),
    /**
     * The user who created this space
     */
    createdBy: z.string(),
    /**
     * The user who last updated the configuration of the space - useful for activities
     * @deprecated this should come from Hive. See querySpace for an example of how to fetch this.
     */
    lastUpdatedBy: z.string().optional(),
    /**
     * List of integrations installed on this space
     */
    integrations: IntegrationInstallationMapSchema.optional(),
    /**
     * Merge rule for this space
     */
    mergeRules: SpaceMergeRulesSchema.optional(),
})
    .and(SpaceWithGitSchema)
    .and(SpaceWithVisitorAuthSchema)
    .and(SpaceSearchKeySchema);
export type Space = z.infer<typeof SpaceSchema>;

/**
 * Normalize a space title.
 */
export function normalizeSpaceTitle(title: string): string {
    return title
        .replace(/(\r\n|\r|\n)/g, ' ')
        .trim()
        .slice(0, SPACE_TITLE_LIMIT)
        .trim();
}

/**
 * Number of days before a soft-deleted space is hard-deleted.
 */
export const SPACE_SOFT_DELETE_DAYS = 7;

/**
 * Return true if the space is soft-deleted.
 */
export function isSoftDeletedSpace(space: Space | api.Space): boolean {
    return !!space.deletedAt;
}

/**
 * Return true if the space is blocked.
 */
export function isBlockedSpace({ blocked }: Space): boolean {
    // For now we don't automatically block spaces with "threats" detected from content-refs
    // to avoid false-positives
    // return space.blockedThreats ? space.blockedThreats.length > 0 : false;

    return Boolean(blocked);
}

/**
 * Assert the current space is active and not deleted.
 */
export function assertSpaceNotDeleted(space: Space) {
    if (isSoftDeletedSpace(space)) {
        throw new createHTTPError.NotFound('Space has been deleted');
    }
}

/**
 * Map a site visibility to a content visibility.
 */
export function siteToContentVisibility(site: api.SiteVisibility): api.ContentVisibility {
    switch (site) {
        case api.SiteVisibility.Public:
            return api.ContentVisibility.Public;
        case api.SiteVisibility.Unlisted:
            return api.ContentVisibility.Unlisted;
        case api.SiteVisibility.ShareLink:
            return api.ContentVisibility.ShareLink;
        case api.SiteVisibility.VisitorAuth:
            return api.ContentVisibility.VisitorAuth;
    }
}
